import { uniq } from 'lodash';
import moment from 'moment';
import 'moment-duration-format';

export const makeString = (arr) => {
  if (arr.length === 1) return arr[0];
  const firsts = uniq(arr).slice(0, arr.length - 1);
  const last = arr[arr.length - 1];
  return firsts.join(', ') + ' and ' + last;
};

function extractContent (s) {
  const span = document.createElement('span');
  span.innerHTML = s;
  return span.textContent || span.innerText || '';
};

/**
   * Check if all work history (experience_info) details combined have >= 500 words
   */
export const getWorkHistoriesStats = (experience_info) => {
  const workHistoriesStats = experience_info
    .map((work_history_item) => {
      const projectsDetailsAndOutcomes = (work_history_item.projects || []).map(project => extractContent([project.details, project.outcome].join(' ')));
      let countArray = [];
      if (work_history_item.details) countArray = [work_history_item.details];
      if (projectsDetailsAndOutcomes && Array.isArray(projectsDetailsAndOutcomes)) countArray = [...countArray, ...projectsDetailsAndOutcomes];
      return {
        WHDetails: work_history_item.details,
        projectsDetailsAndOutcomes: projectsDetailsAndOutcomes,
        wordCount: countArray.length > 0 ? countArray.join(' ').split(/\s+/).filter(v => v.length).length : 0/** Only split if the array length > 0; Split in spaces (tab, newline, whitespace etc) */
      };
    });
  const workHistoriesWordCount = workHistoriesStats.map(stats => stats.wordCount).reduce((acc, value) => { acc += typeof value === 'number' ? value : 0; return acc; }, 0);
  return workHistoriesWordCount;
};

/**
   * Returns a map with statistics for each vetted skills (passed skills):
   *  - Mentions count in WH details + Projects details combined;
   *  - YoE for each WH that references that skill in its details + projects details combined;
   * */

export const getVettedSkillsStats = (passed_skills, experience_info) => {
  const vettedSkillsStats = passed_skills.map((skill) => {
    let skillMentionsCount = 0;
    const skillMentionsList = [];
    let skillYOE = 0;
    let skillMOE = 0;
    experience_info.forEach((work_history) => {
      const { projects } = work_history;
      projects.forEach((project) => {
        let { details, outcome, start_year, start_month, end_year, end_month, skills } = project;
        const isInProjectSkills = skills.find(({ skill_id }) => skill_id === skill.skill_id);
        if (isInProjectSkills) {
          skillMentionsList.push({
            work_history_id: work_history.id,
            project_id: project.id,
            title: project.project_name
          });
        }
        // Concatenate Projects Details + Project Outcome
        const textToBeMatched = [details || '', outcome || ''].join(' ').toLowerCase();
        // Find all the exact matches using RegExp
        // TODO: fuzzy matching, so it supports variations of the skill name?
        const n = skill.skill_name
          .split(/[,]/) // split only on commas
          .map(name => name.toLowerCase().trim())
          .filter(name => name)
          // eslint-disable-next-line no-useless-escape
          .map(name => name.replace(/([^a-zA-Z0-9\.\s\-\/])/g, '\\$1'))
          // eslint-disable-next-line no-useless-escape
          .map(name => name.replace(/([\.\s\-])/g, '[\\.\\s\\-]*?'))
          .map(name => new RegExp('(?<=^|\\s)' + (name.length > 2 ? name.replace(/\b(js)\b/g, '($1)?') : name) + '(?=[\\.,]?\\s|$)', 'gim'))
          .map(regex => textToBeMatched.match(regex))
          .reduce((acc, result) => {
            if (Array.isArray(result)) {
              return Math.min(result.length, acc);
            }
            return 0;
          }, Infinity);
        // Discard if no match
        if ((n === Infinity || n === 0) && !isInProjectSkills) return;
        // Adds the number of references to the skill mentions count
        skillMentionsCount += n;
        // Calculates years of experience for that skill
        if (!end_year) {
          end_year = (new Date()).getFullYear();
        }
        const start_date = moment(`${start_month} ${start_year}`, 'MMMM YYYY');
        const end_date = moment(`${end_month} ${end_year}`, 'MMMM YYYY');
        skillYOE += moment.duration(end_date.diff(start_date)).as('hours') / 24 / 365;
        skillMOE += Math.round(moment.duration(end_date.diff(start_date)).as('months'));
      });
    });
    return { skill, skill_name: skill.skill_name, skillMentionsCount, skillMentionsList, skillYOE, skillMOE };
  });
  /**
     * Build an array containing vetted skills with less than 3 mentions.
     */
  const vettedSkillsNotReferencedAtLeast3Times = vettedSkillsStats.filter((vettedSkill) => {
    return vettedSkill.skillMentionsCount < 3;
  });

  /**
   * Build an array containing vetted skills with less than 2 YoE.
   */
  const vettedSkillsWithLessThan2YOE = vettedSkillsStats.filter((vettedSkill) => {
    return vettedSkill.skillYOE < 2;
  });
  return { vettedSkillsStats, vettedSkillsNotReferencedAtLeast3Times, vettedSkillsWithLessThan2YOE };
};
